import {
    createContext,
    useContext,
    useMemo,
    ReactNode,
    ComponentType,
    FC,
    useCallback,
} from 'react';
import { toUpper } from 'lodash';

type UIContextType = {
    isLegacyUI?: boolean;
    transformLabel?: (label: string) => string;
};

export const UIContext = createContext<UIContextType>({
    isLegacyUI: false,
    transformLabel: (label: string) => label,
});

export const UIProvider = ({
    children,
    initialLegacyUI = false,
}: {
    children: ReactNode;
    initialLegacyUI?: boolean;
}) => {
    const transformLabel = useCallback(
        (label: string) => (initialLegacyUI ? toUpper(label) : label),
        [initialLegacyUI]
    );
    const value = useMemo(
        () => ({ isLegacyUI: initialLegacyUI, transformLabel }),
        [initialLegacyUI, transformLabel]
    );

    return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};

export const useUI = (): UIContextType => {
    const context = useContext(UIContext);
    return context ?? { isLegacyUI: false }; // fallback to modern UI
};

export function withUI<P>(
    Component: ComponentType<P & UIContextType>
): FC<Omit<P, keyof UIContextType>> {
    return function ComponentWithUI(props: Omit<P, keyof UIContextType>) {
        const { isLegacyUI } = useUI();
        return <Component {...(props as P)} isLegacyUI={isLegacyUI} />;
    };
}
